import { compareOrder } from './ordering';
/** Direct children of `id`, sorted by their fractional index. */
export function childrenOf(doc, id) {
    const children = [];
    for (const node of Object.values(doc.nodes)) {
        if (node.parentId === id)
            children.push(node);
    }
    children.sort((a, b) => compareOrder(a.order, b.order));
    return children;
}
export function childIdsOf(doc, id) {
    return childrenOf(doc, id).map((node) => node.id);
}
export function parentOf(doc, id) {
    const parentId = doc.nodes[id]?.parentId;
    return parentId === null || parentId === undefined ? undefined : doc.nodes[parentId];
}
/** Siblings of `id`, including `id` itself, in order. */
export function siblingsOf(doc, id) {
    const node = doc.nodes[id];
    if (node === undefined)
        return [];
    if (node.parentId === null)
        return [node];
    return childrenOf(doc, node.parentId);
}
/**
 * Root-first path from the root down to `id`, inclusive.
 *
 * Bounded by the node count: a corrupted document with a parent cycle must not hang the editor.
 * `invariants.ts` is what detects that condition; this only refuses to spin on it.
 */
export function pathToRoot(doc, id) {
    const path = [];
    const limit = Object.keys(doc.nodes).length + 1;
    let current = doc.nodes[id];
    let steps = 0;
    while (current !== undefined && steps < limit) {
        path.push(current);
        if (current.parentId === null)
            break;
        current = doc.nodes[current.parentId];
        steps += 1;
    }
    return path.reverse();
}
/** Distance from the root. The root is 0. */
export function depthOf(doc, id) {
    return Math.max(0, pathToRoot(doc, id).length - 1);
}
/**
 * The subtree rooted at `id`, depth-first, `id` first.
 *
 * `includeCollapsed` defaults to true because this is the structural traversal — deletion,
 * export and integrity checks must see hidden nodes. Rendering passes false.
 */
export function subtree(doc, id, options) {
    const includeCollapsed = options?.includeCollapsed ?? true;
    const out = [];
    const start = doc.nodes[id];
    if (start === undefined)
        return out;
    const visit = (node) => {
        out.push(node);
        if (!includeCollapsed && node.collapsed)
            return;
        for (const child of childrenOf(doc, node.id))
            visit(child);
    };
    visit(start);
    return out;
}
export function subtreeIds(doc, id, options) {
    return subtree(doc, id, options).map((node) => node.id);
}
/** Is `candidate` anywhere below `ancestor`? A node is not its own descendant. */
export function isDescendantOf(doc, ancestor, candidate) {
    if (ancestor === candidate)
        return false;
    const limit = Object.keys(doc.nodes).length + 1;
    let current = doc.nodes[candidate]?.parentId ?? null;
    let steps = 0;
    while (current !== null && steps < limit) {
        if (current === ancestor)
            return true;
        current = doc.nodes[current]?.parentId ?? null;
        steps += 1;
    }
    return false;
}
/**
 * Would reparenting `nodeId` under `newParentId` be legal?
 *
 * Three ways it is not: moving a node into itself, into its own subtree (both create a cycle no
 * traversal can terminate on), or moving the root at all. The command layer asks this before
 * mutating; the drag gesture asks it to decide whether to show a valid drop target.
 */
export function canReparent(doc, nodeId, newParentId) {
    if (nodeId === newParentId)
        return false;
    if (doc.nodes[nodeId] === undefined || doc.nodes[newParentId] === undefined)
        return false;
    if (doc.map.rootNodeId === nodeId)
        return false;
    return !isDescendantOf(doc, nodeId, newParentId);
}
/** Depth-first order over the whole map, root first. */
export function walk(doc, options) {
    return subtree(doc, doc.map.rootNodeId, options);
}
/** Nodes with no children. */
export function leavesOf(doc, id) {
    return subtree(doc, id).filter((node) => childrenOf(doc, node.id).length === 0);
}
/**
 * Leaf count per node, in one pass.
 *
 * Layout engines size a branch's angular sector or vertical extent from this, and calling
 * `leavesOf` per node would make that quadratic on exactly the maps where it matters.
 */
export function leafCounts(doc) {
    const counts = {};
    const order = walk(doc);
    for (let i = order.length - 1; i >= 0; i--) {
        const node = order[i];
        if (node === undefined)
            continue;
        const children = childrenOf(doc, node.id);
        counts[node.id] =
            children.length === 0 ? 1 : children.reduce((sum, c) => sum + (counts[c.id] ?? 1), 0);
    }
    return counts;
}
//# sourceMappingURL=tree.js.map